import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc, // Add this import
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "./firebase"; // Add auth import

// Add a new workout
export const addWorkout = async (userId, workoutData) => {
  try {
    console.log('Adding workout for user:', userId);
    console.log('Workout data:', workoutData);

    const points = workoutData.exercises.length; // 1 point per exercise

    const docRef = await addDoc(collection(db, "workouts"), {
      userId,
      exercises: workoutData.exercises,
      createdAt: serverTimestamp(),
      date: workoutData.date || new Date().toISOString().split("T")[0], // Use custom date or default to today
      totalExercises: workoutData.exercises.length,
      points: points, // Points = number of exercises
    });

    console.log('Workout added successfully with ID:', docRef.id);

    // Update user stats
    await updateUserStats(userId, workoutData.exercises);

    return docRef.id;
  } catch (error) {
    console.error("Error adding workout:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    throw error;
  }
};

// Get user's workouts
export const getUserWorkouts = async (userId) => {
  try {
    const q = query(
      collection(db, "workouts"),
      where("userId", "==", userId)
      // Remove orderBy to avoid index requirement
    );

    const querySnapshot = await getDocs(q);
    const workouts = [];

    querySnapshot.forEach((doc) => {
      workouts.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort by date (newest first), then by creation time for same dates
    workouts.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (dateA.getTime() === dateB.getTime()) {
        // Same date, sort by creation time
        return new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt);
      }

      // Different dates, sort by workout date
      return dateB - dateA;
    });

    return workouts;
  } catch (error) {
    console.error("Error getting workouts:", error);
    throw error;
  }
};

// Get user stats
export const getUserStats = async (userId) => {
  try {
    const workouts = await getUserWorkouts(userId);

    const totalWorkouts = workouts.length;
    const totalPoints = workouts.reduce(
      (sum, workout) => sum + (workout.points || 0),
      0
    );

    // Calculate streak
    const streak = calculateStreak(workouts);

    return {
      totalWorkouts,
      totalPoints,
      streak,
      recentWorkouts: workouts.slice(0, 5),
    };
  } catch (error) {
    console.error("Error getting user stats:", error);
    throw error;
  }
};

// Update user stats (for points tracking) - ENHANCED with streak calculation
const updateUserStats = async (userId, exercises) => {
  try {
    console.log('Updating user stats for:', userId);

    const userStatsRef = doc(db, "userStats", userId);
    const userStatsDoc = await getDoc(userStatsRef);

    const points = exercises.length; // 1 point per exercise
    console.log('Calculated points:', points);

    // Get user info from auth to store display name and photo
    const user = auth.currentUser;

    // Calculate current streak by getting user's workouts
    const userWorkouts = await getUserWorkouts(userId);
    const currentStreak = calculateStreak(userWorkouts);

    if (userStatsDoc.exists()) {
      await updateDoc(userStatsRef, {
        totalWorkouts: increment(1),
        totalPoints: increment(points), // Increment by number of exercises
        lastWorkout: serverTimestamp(),
        streak: currentStreak, // Store calculated streak
        // Update display name and photo in case they changed
        displayName: user?.displayName || 'Anonymous User',
        photoURL: user?.photoURL || null,
      });
      console.log('User stats updated successfully');
    } else {
      // Create new document if it doesn't exist
      await setDoc(userStatsRef, {
        userId,
        totalWorkouts: 1,
        totalPoints: points, // Points = number of exercises in first workout
        lastWorkout: serverTimestamp(),
        streak: currentStreak, // Store calculated streak
        displayName: user?.displayName || 'Anonymous User',
        photoURL: user?.photoURL || null,
      });
      console.log('New user stats created successfully');
    }
  } catch (error) {
    console.error("Error updating user stats:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    throw error;
  }
};

// Delete a workout
export const deleteWorkout = async (workoutId, userId) => {
  try {
    console.log('Deleting workout:', workoutId);

    // Get the workout data before deleting to update user stats
    const workoutRef = doc(db, "workouts", workoutId);
    const workoutDoc = await getDoc(workoutRef);

    if (!workoutDoc.exists()) {
      throw new Error('Workout not found');
    }

    const workoutData = workoutDoc.data();

    // Verify the workout belongs to the user
    if (workoutData.userId !== userId) {
      throw new Error('Unauthorized: Cannot delete another user\'s workout');
    }

    // Delete the workout document
    await deleteDoc(workoutRef);
    console.log('Workout deleted successfully');

    // Update user stats (subtract the points and workout count)
    await updateUserStatsAfterDelete(userId, workoutData.points || workoutData.exercises.length);

    return true;
  } catch (error) {
    console.error("Error deleting workout:", error);
    throw error;
  }
};

// Update user stats after deletion
const updateUserStatsAfterDelete = async (userId, pointsToSubtract) => {
  try {
    const userStatsRef = doc(db, "userStats", userId);
    const userStatsDoc = await getDoc(userStatsRef);

    if (userStatsDoc.exists()) {
      // Recalculate streak by getting updated workout list
      const userWorkouts = await getUserWorkouts(userId);
      const newStreak = calculateStreak(userWorkouts);

      await updateDoc(userStatsRef, {
        totalWorkouts: increment(-1), // Subtract 1 workout
        totalPoints: increment(-pointsToSubtract), // Subtract points
        streak: newStreak, // Recalculated streak
        lastWorkout: serverTimestamp(),
      });
      console.log('User stats updated after deletion');
    }
  } catch (error) {
    console.error("Error updating user stats after deletion:", error);
    throw error;
  }
};

// Calculate workout streak - SIMPLIFIED
const calculateStreak = (workouts) => {
  if (workouts.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get unique workout dates (in case multiple workouts per day)
  const uniqueDates = [...new Set(workouts.map(w => w.date))].sort((a, b) => new Date(b) - new Date(a));

  let streak = 0;
  let currentDate = new Date(today);

  for (const dateStr of uniqueDates) {
    const workoutDate = new Date(dateStr);
    workoutDate.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor((currentDate - workoutDate) / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) {
      // Workout on current expected date
      streak++;
      currentDate.setDate(currentDate.getDate() - 1); // Move to previous day
    } else if (daysDiff === 1 && streak === 0) {
      // First workout was yesterday (still counts as current streak)
      streak++;
      currentDate.setDate(currentDate.getDate() - 2); // Move to day before yesterday
    } else {
      // Gap found - streak ends
      break;
    }
  }

  return streak;
};

// Get leaderboard data - SIMPLE (streak now stored in userStats)
export const getLeaderboard = async () => {
  try {
    const q = query(
      collection(db, "userStats"),
      orderBy("totalPoints", "desc")
    );

    const querySnapshot = await getDocs(q);
    const leaderboard = [];

    querySnapshot.forEach((doc) => {
      leaderboard.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return leaderboard;
  } catch (error) {
    console.error("Error getting leaderboard:", error);
    throw error;
  }
};

// Get workout videos
export const getWorkoutVideos = async () => {
  try {
    const q = query(
      collection(db, "workoutVideos"),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const videos = [];

    querySnapshot.forEach((doc) => {
      videos.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return videos;
  } catch (error) {
    console.error("Error fetching workout videos:", error);
    return [];
  }
};

// Add a workout video (for admin use)
export const addWorkoutVideo = async (videoData) => {
  try {
    const docRef = await addDoc(collection(db, "workoutVideos"), {
      ...videoData,
      createdAt: serverTimestamp(),
    });

    console.log('Workout video added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error adding workout video:", error);
    throw error;
  }
};

// Delete workout video (admin only)
export const deleteWorkoutVideo = async (videoId) => {
  try {
    await deleteDoc(doc(db, "workoutVideos", videoId));
    console.log('Workout video deleted successfully');
  } catch (error) {
    console.error("Error deleting workout video:", error);
    throw error;
  }
};